import React, { lazy, Suspense } from 'react';
import { ClientOnly } from '@tanstack/react-router';
import sanitizeHtml from '../../utils/sanitize';
import { mergeWeatherData } from './mergeWeatherData';
import WeatherCombinedChart from './WeatherCombinedChart';
import RatingHeatmapTimeline from './RatingHeatmapTimeline';
import './cards/index.scss';

const LazyLeafletMap = lazy(() => import('../utils/Maps/LeafletMap'));
import MapSkeleton from '../utils/Maps/MapSkeleton';

const NAIROBI_SENTINEL_LAT = -1.2920659;

/*
Renders one useful-info article's full content: kicker/title, weather chart,
best-time-to-visit heatmap, image gallery, location map and the rich-text
body. Shared by the single-article page and the all-in-one guide listing so
both stay visually and behaviourally identical.
*/
const ArticleBody = ({ article, destinationName, headingLevel = 'h3' }) => {
  if (!article) return null;

  const detailed = article.detailed || {};
  const bestTimeToVisit = Array.isArray(detailed.bestTimeToVisit) ? detailed.bestTimeToVisit : [];
  const canShowBestTimeToVisit = bestTimeToVisit.length > 0
    && !bestTimeToVisit.every((item) => item.rating === 'Excellent');
  const hasWeatherChart = Array.isArray(article.averageMaxMonthlyTemperature)
    && article.averageMaxMonthlyTemperature.length > 0;
  const images = Array.isArray(detailed.images) ? detailed.images : [];
  const hasLocation = detailed.location && detailed.location.latitude !== NAIROBI_SENTINEL_LAT;
  const hasRainfallData = Array.isArray(article.averageMonthlyRainfall) && article.averageMonthlyRainfall.length > 0;

  const descriptionHtml = sanitizeHtml(
    (article.description || '')
      .replace(/<p>(?:\s|&nbsp;)*<br\s*\/?>(?:\s|&nbsp;)*(?:<\/p>)?/gi, '<div class="usefulinfo-cards__description-text__spacer"></div>')
      .replace(/&nbsp;/gi, ' ')
  );

  const TitleTag = headingLevel;

  return (
    <div className="usefulinfo-cards__description-content">
      {article.title && (
        <>
          <div className="usefulinfo-cards__kicker">
            {article.eyebrow || (destinationName ? `${destinationName} Guide` : 'Useful Information')}
          </div>
          <TitleTag className="usefulinfo-cards__description-title">{article.title}</TitleTag>
        </>
      )}

      {/* Weather Chart */}
      {hasWeatherChart && (
        <div className="usefulinfo-cards__card">
          <h4 className="usefulinfo-cards__block-label">Average Weather</h4>
          <div className="usefulinfo-cards__weather-chart">
            <WeatherCombinedChart
              data={mergeWeatherData({
                averageMonthlyRainfall: article.averageMonthlyRainfall || [],
                averageMinMonthlyTemperature: article.averageMinMonthlyTemperature || [],
                averageMaxMonthlyTemperature: article.averageMaxMonthlyTemperature
              })}
            />
          </div>
        </div>
      )}

      {/* Best time to visit heatmap */}
      {canShowBestTimeToVisit && (
        <div className="usefulinfo-cards__card">
          <h4 className="usefulinfo-cards__block-label">Best Time to Visit</h4>
          <div className="usefulinfo-cards__heatmap">
            <RatingHeatmapTimeline data={bestTimeToVisit} />
          </div>
        </div>
      )}

      {/* Images gallery */}
      {images.length > 0 && (
        <div className="usefulinfo-cards__images">
          {images.map((img, idx) => (
            <div className="usefulinfo-cards__image-item" key={idx}>
              <img src={img.url} alt={`${article.title} ${idx + 1}`} loading="lazy" />
            </div>
          ))}
        </div>
      )}

      {/* Country map if no rainfall data */}
      {hasLocation && !hasRainfallData && (
        <div className="usefulinfo-cards__map-wrap">
          <ClientOnly fallback={<MapSkeleton label="Loading country map…" />}>
            <Suspense fallback={<MapSkeleton label="Initializing map…" />}>
              <LazyLeafletMap
                latitude={detailed.location.latitude}
                longitude={detailed.location.longitude}
                zoom={5}
              />
            </Suspense>
          </ClientOnly>
        </div>
      )}

      <div className="usefulinfo-cards__description-text">
        <div className="inserted-html" dangerouslySetInnerHTML={{ __html: descriptionHtml }}></div>
      </div>
    </div>
  );
};

export default ArticleBody;
